import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { FaMapLocationDot } from "react-icons/fa6";
import { PiPolygonFill } from "react-icons/pi";
import Btn from "../shared/button/Btn";
import SmallSpinner from "../shared/spinners/SmallSpinner";
import InputTextarea from "../shared/input/new/InputTextarea";
import InputCityTown from "../shared/input/new/InputCityTown";
import InputText from "../shared/input/new/InputText";
import { useScrollLock } from "../../hooks/useScrollLock";
import { showModal, showNestedModal } from "../../features/modal/modalSlice";
import { handleActiveSelect } from "../../features/layout/layoutSlice";
import PageTopTitle from "../shared/PageTopTitle";
import {
  addRegisteredAddress,
  clearAddressData,
  setIsLocationInsidePolygon,
  updateAddressData,
} from "../../features/registeredAddress/registeredAddressSlice";

const ModalContainer = styled.div`
  position: fixed;
  inset: 0;
  z-index: 26;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.6);

  .modal {
    position: relative;
    width: 800px;
    max-width: 900px;
    min-height: 630px;
    padding-top: 20px;
    border-radius: 10px;
    color: ${({ theme }) => theme.mainTextColor};
    background: ${({ theme }) => theme.mainBgColor};
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .content {
    width: 100%;
    max-height: 500px;
    overflow: auto;
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .desc {
    margin: 20px 70px;
    padding: 20px;
    border-radius: 10px;
    background: ${({ theme }) => theme.secondaryBgColor};
    font-size: 15px;
    font-weight: var(--font-weight-lg);
    text-align: center;
  }

  .input-list {
    width: 60%;
    display: flex;
    flex-direction: column;
    gap: 20px;
  }

  .input-item {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 5px;
  }

  .location-row {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: 15px;
  }

  .google-adres-container {
    width: auto;
    max-width: 400px;
    min-height: 54px;
    padding: 5px 10px;
    border: 1px solid ${({ theme }) => theme.mainBorderColor};
    border-radius: 5px;
    background: ${({ theme }) => theme.secondaryBgColor};
    font-size: 12px;
    font-weight: var(--font-weight-md);
  }

  .google-adres-top {
    margin-bottom: 5px;
    font-weight: var(--font-weight-lg);
    text-decoration: underline;
  }

  .redirect-span {
    display: flex;
    align-items: center;
    gap: 5px;
    cursor: pointer;
    font-size: 12px;
    font-weight: var(--font-weight-lg);
  }

  .location-icon {
    font-size: 1.6rem;
  }

  .pol-desc {
    padding: 10px;
    background: ${({ theme }) => theme.secondaryBgColor};
    font-size: 13px;
    font-weight: var(--font-weight-md);
    line-height: 1.6;
  }

  .err-message {
    padding-left: 4px;
    color: var(--color-main);
    font-size: 13px;
    font-weight: var(--font-weight-lg);
  }

  .polygon-actions {
    display: flex;
    justify-content: space-between;
    padding-top: 8px;
  }

  .polygon-actions button {
    text-decoration: underline;
    font-size: 13px;
    font-weight: 600;
  }

  .button-grp {
    position: absolute;
    right: 0;
    bottom: 0;
    left: 0;
    margin: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
  }

  @media (max-width: 850px) {
    .modal {
      width: 100vw;
      height: 100dvh;
      min-height: 100dvh;
      border-radius: 0;
    }

    .desc {
      width: 95%;
      margin-inline: 0;
    }

    .input-list {
      width: 90%;
    }
  }
`;

const pointInPolygon = (point, polygon) => {
  let inside = false;
  const x = point.lng;
  const y = point.lat;

  for (let index = 0, previous = polygon.length - 1; index < polygon.length; previous = index++) {
    const xi = Number(polygon[index].lng);
    const yi = Number(polygon[index].lat);
    const xj = Number(polygon[previous].lng);
    const yj = Number(polygon[previous].lat);
    const intersects =
      (yi > y) !== (yj > y) && x <= ((xj - xi) * (y - yi)) / (yj - yi) + xi;
    if (intersects) inside = !inside;
  }
  return inside;
};

const KayitliAdresEkleNewModal = () => {
  const dispatch = useDispatch();
  const path = useLocation().pathname;
  const { modalLocation } = useSelector((state) => state.modal);
  const {
    isLoadingAddRegisteredAddress,
    addressData,
    isLocationInsidePolygon,
  } = useSelector((state) => state.registeredAddress);
  const { lockScroll, unlockScroll } = useScrollLock();
  const [isSubmitted, setIsSubmitted] = useState(false);

  useEffect(() => {
    lockScroll();
    return () => {
      unlockScroll();
      dispatch(clearAddressData());
    };
  }, [dispatch, lockScroll, unlockScroll]);

  useEffect(() => {
    if (path !== modalLocation) dispatch(showModal(null));
  }, [dispatch, path, modalLocation]);

  useEffect(() => {
    const lat = Number(addressData?.locationData?.lat);
    const lng = Number(addressData?.locationData?.lng);
    const polygon = Array.isArray(addressData?.polygonCoords)
      ? addressData.polygonCoords
          .slice()
          .sort((a, b) => (a?.sortOrder ?? 0) - (b?.sortOrder ?? 0))
          .map((point) => ({ lat: Number(point.lat), lng: Number(point.lng) }))
      : [];

    if (!Number.isFinite(lat) || !Number.isFinite(lng) || polygon.length < 3) {
      dispatch(setIsLocationInsidePolygon(false));
      return;
    }

    let inside;
    if (window.google?.maps?.geometry?.poly?.containsLocation) {
      const googlePolygon = new window.google.maps.Polygon({ paths: polygon });
      inside = window.google.maps.geometry.poly.containsLocation(
        new window.google.maps.LatLng(lat, lng),
        googlePolygon
      );
    } else {
      inside = pointInPolygon({ lat, lng }, polygon);
    }
    dispatch(setIsLocationInsidePolygon(inside));
  }, [
    dispatch,
    addressData?.locationData?.lat,
    addressData?.locationData?.lng,
    addressData?.polygonCoords,
  ]);

  const handleSubmit = () => {
    setIsSubmitted(true);
    const isValid =
      addressData?.locationData?.location &&
      addressData?.locationData?.lat &&
      addressData?.locationData?.lng &&
      addressData?.saveName?.trim() &&
      addressData?.address?.trim() &&
      addressData?.addressDescription?.trim();

    if (!isValid) return;

    const payload = {
      addressName: addressData.saveName,
      addressCity: addressData.locationData.city,
      addressTown: addressData.locationData.town,
      address: addressData.address,
      addressLat: addressData.locationData.lat.toString(),
      addressLong: addressData.locationData.lng.toString(),
      locationId: addressData.locationData.townId?.toString() ?? "",
      addressInfo: addressData.addressDescription,
      mapAddressLat: addressData.locationData.lat.toString(),
      mapAddressLong: addressData.locationData.lng.toString(),
      mapAddressName: addressData.locationData.googleAddress ?? "",
    };

    if (addressData.polygonCoords.length >= 3) {
      payload.polygonPoints = addressData.polygonCoords.map((point, index) => ({
          sortOrder: index,
          latitude: Number(point.lat),
          longitude: Number(point.lng),
      }));
    }

    dispatch(addRegisteredAddress(payload));
  };

  const polygonLength = addressData?.polygonCoords?.length ?? 0;
  const invalidPolygon = polygonLength >= 3 && !isLocationInsidePolygon;

  return (
    <ModalContainer onClick={() => dispatch(showModal(null))}>
      <div
        className="modal"
        onClick={(event) => {
          event.stopPropagation();
          dispatch(handleActiveSelect(null));
        }}
      >
        <PageTopTitle title="Kayıtlı Adres Oluştur" />
        <div className="content">
          <p className="desc">
            Yükleme veya dağıtım noktalarını kayıtlı adres olarak kaydedebilirsiniz.
            Kaydettiğiniz bilgileri yük kaydı girişlerinde kullanabilirsiniz.
          </p>

          <div className="input-list">
            <div className="input-item">
              <InputText
                data={addressData?.saveName}
                setData={(event) => dispatch(updateAddressData({ saveName: event.target.value }))}
                inputWidth="100%"
                labelText="Adres İsmi (Zorunlu)"
                name="adresName"
              />
              {isSubmitted && !addressData?.saveName?.trim() && (
                <span className="err-message">Adres İsmi alanını giriniz.</span>
              )}
            </div>

            <div className="input-item">
              <InputCityTown
                data={addressData?.locationData?.location}
                setData={(selectedLocation) =>
                  dispatch(
                    updateAddressData({
                      locationData: {
                        ...selectedLocation,
                        googleAddress: "",
                      },
                    })
                  )
                }
                inputWidth="100%"
                labelText="İlçe/İl (Zorunlu)"
                name="location"
              />
              {isSubmitted && !addressData?.locationData?.location && (
                <span className="err-message">İlçe/İl alanını giriniz.</span>
              )}
            </div>

            <div className="input-item location-row">
              <span
                className="redirect-span"
                onClick={() => dispatch(showNestedModal("registeredAddressAbsoluteLocationMap"))}
              >
                <FaMapLocationDot className="location-icon" />
                Haritadan Konum Seç
              </span>
              {addressData?.locationData?.googleAddress && (
                <div className="google-adres-container">
                  <div className="google-adres-top">Seçilen Adres</div>
                  <div>{addressData.locationData.googleAddress}</div>
                </div>
              )}
            </div>

            {addressData?.locationData?.lat && addressData?.locationData?.lng && (
              <div className="input-item">
                <span
                  className="redirect-span"
                  onClick={() => dispatch(showNestedModal("registeredAddressPolygonMap"))}
                >
                  <PiPolygonFill className="location-icon" />
                  {polygonLength >= 3 ? "Poligon Düzenle" : "Poligon Çiz"}
                </span>

                {polygonLength < 3 && (
                  <>
                    <div className="pol-desc">Seçtiğiniz konumu bir poligon çizerek çevreleyin.</div>
                    <div className="pol-desc">Çizilen poligon seçtiğiniz konumu kapsamalıdır.</div>
                  </>
                )}

                {invalidPolygon && (
                  <div className="pol-desc" style={{ backgroundColor: "#e247473d" }}>
                    <div>Hatalı Poligon (Çizilen poligon seçilen konumu kapsamıyor.)</div>
                    <div className="polygon-actions">
                      <button type="button" onClick={() => dispatch(showNestedModal("registeredAddressPolygonMap"))}>
                        Poligonu Düzenle
                      </button>
                      <button type="button" onClick={() => dispatch(updateAddressData({ polygonCoords: [] }))}>
                        Poligonu Kaldır
                      </button>
                    </div>
                  </div>
                )}

                {polygonLength >= 3 && isLocationInsidePolygon && (
                  <div className="pol-desc" style={{ backgroundColor: "#93e73340" }}>
                    Geçerli Poligon (Çizilen poligon seçilen konumu kapsıyor.)
                  </div>
                )}
              </div>
            )}

            <div className="input-item">
              <InputTextarea
                data={addressData?.address}
                setData={(event) => dispatch(updateAddressData({ address: event.target.value }))}
                inputWidth="100%"
                labelText="Adres (Zorunlu)"
                name="address"
              />
              {isSubmitted && !addressData?.address?.trim() && (
                <span className="err-message">Adres alanını giriniz.</span>
              )}
            </div>

            <div className="input-item">
              <InputTextarea
                data={addressData?.addressDescription}
                setData={(event) => dispatch(updateAddressData({ addressDescription: event.target.value }))}
                inputWidth="100%"
                labelText="Adres Tarifi (Zorunlu)"
                name="addressDescription"
              />
              {isSubmitted && !addressData?.addressDescription?.trim() && (
                <span className="err-message">Adres Tarifi alanını giriniz.</span>
              )}
            </div>
          </div>
        </div>

        <div className="button-grp">
          <Btn
            bgColor={invalidPolygon ? "#7c7b7b" : "#E85446"}
            borderColor="none"
            color="#fff"
            width="280px"
            isDisabled={invalidPolygon}
            handleClick={handleSubmit}
          >
            {isLoadingAddRegisteredAddress ? (
              <SmallSpinner size="20px" color="#E85446" bgColor="#fff" />
            ) : (
              "Kayıtlı Adreslerime Kaydet"
            )}
          </Btn>
          <Btn width="280px" bgColor="#fff" color="#E85546" handleClick={() => dispatch(showModal(null))}>
            Vazgeç
          </Btn>
        </div>
      </div>
    </ModalContainer>
  );
};

export default KayitliAdresEkleNewModal;
